<template>
  <div>
    <Head
      :title="`${seoTitle} - ${formatMoney(auction.price)}`"
      :description="seoDescription"
      :keywords="seoKeywords"
      :json-ld="auction.json_ld"
    />

    <Container>
      <div class="container mx-auto py-8">
        <div class="flex md:flex-row flex-col items-baseline justify-between">
          <div class="order-2 md:order-1 w-full lg:w-auto mt-4 md:mt-0">
            <h1 class="font-bold text-4xl mb-1">
              {{ auction.name }}
            </h1>

            <div class="flex lg:block flex-row md:flex-col justify-between items-center mb-4">
              <Link
                class="underline hover:no-underline"
                :href="route('frontend.theme-auctions.show', {themeAuction: themeAuction.slug})"
              >
                {{ themeAuction.name }}
              </Link>

              <div class="lg:hidden">
                <AuctionFavorite :auction="auction" />
              </div>
            </div>
          </div>
          <div class="flex md:block justify-between w-full md:w-auto order-1 md:order-2 md:space-x-4 text-sm md:text-base">
            <Link
              class="hover:underline"
              :href="route('frontend.auctions.navigate', {themeAuction: themeAuction.slug, auction: auction.slug, direction: 'previous'})"
            >
              <i class="fa fa-chevron-left mr-1" />Vorige kavel
            </Link>
            <Link
              class="hover:underline"
              :href="route('frontend.auctions.navigate', {themeAuction: themeAuction.slug, auction: auction.slug, direction: 'next'})"
            >
              Volgende kavel<i class="fa fa-chevron-right ml-1" />
            </Link>
          </div>
        </div>

        <div class="p-4 bg-white rounded">
          <div class="flex flex-col lg:flex-row w-full lg:space-x-8">
            <div class="w-full lg:w-1/2">
              <div class="lg:hidden bg-sky-500 rounded text-center w-full py-2">
                <div class="w-[240px] mx-auto bg-white rounded px-4 pt-1 text-4xl">
                  <time
                    data-format="short"
                    :datetime="auction.auction_ends_at"
                  >
                    {{ auction.auction_ends_at }}
                  </time>
                </div>
              </div>

              <ul
                id="slider-big"
                class="my-6"
              >
                <li v-if="auction.main_image_big">
                  <img :src="auction.main_image_big">
                </li>
                <li
                  v-for="image in auction.images_big"
                  :key="image"
                >
                  <img :src="image">
                </li>
              </ul>

              <ul
                id="slider-controls"
                class="relative"
              >
                <li class="prev absolute inset-y-0 left-0 top-8">
                  <i class="fa fa-chevron-left" />
                </li>
                <li class="next absolute inset-y-0 right-0 top-8">
                  <i class="fa fa-chevron-right" />
                </li>
              </ul>

              <ul
                id="slider-small"
                class="text-center"
              >
                <li
                  v-if="auction.main_image_small"
                  class="w-16 md:w-24 inline-block text-center"
                >
                  <img
                    :src="auction.main_image_small"
                    :alt="auction.name"
                    class="m-auto"
                  >
                </li>
                <li
                  v-for="image in auction.images_small"
                  :key="image"
                  class="w-16 md:w-24 inline-block text-center"
                >
                  <img
                    :src="image"
                    :alt="auction.name"
                    class="m-auto"
                  >
                </li>
              </ul>
            </div>
            <div class="w-full lg:w-1/2 flex flex-col">
              <div class="grow">
                <div class="hidden lg:block bg-sky-500 rounded text-center w-full py-2 mb-4">
                  <div class="w-[240px] mx-auto bg-white rounded px-4 pt-1 text-4xl">
                    <time
                      data-format="short"
                      :datetime="auction.auction_ends_at"
                    >
                      {{ auction.auction_ends_at }}
                    </time>
                  </div>
                </div>

                <LargerPlaceBid
                  :item="auction"
                  :enable-autobid="true"
                  :hide-bids="true"
                  @auction-updated="auctionUpdated"
                />
              </div>

              <div class="flex flex-row lg:flex-col items-center lg:items-end justify-between text-sm mt-4 lg:mt-0">
                <template v-if="auction.label === 'A-GRADE'">
                  <span class="px-1 border border-gray-400 bg-gray-100 text-sm text-gray-350 uppercase"><span class="text-green-500">A</span>-Grade</span>
                </template>
                <template v-else-if="auction.label === 'B-GRADE'">
                  <span class="px-1 border border-gray-400 bg-gray-100 text-sm text-gray-350 uppercase"><span class="text-yellow-500">B</span>-Grade</span>
                </template>
                <template v-else-if="auction.label === 'C-GRADE'">
                  <span class="px-1 border border-gray-400 bg-gray-100 text-sm text-gray-350 uppercase"><span class="text-pink-400">C</span>-Grade</span>
                </template>
                <template v-else-if="auction.label === 'D-GRADE'">
                  <span class="px-1 border border-gray-400 bg-gray-100 text-sm text-gray-350 uppercase"><span class="text-red-500">D</span>-Grade</span>
                </template>
                <template v-else>
                  <span class="px-1 border border-gray-400 bg-gray-100 text-sm text-gray-350 uppercase">{{ auction.label }}</span>
                </template>

                <div class="lg:mt-2">
                  {{ auction.bid_count }} biedingen - {{ auction.views }}x bekeken
                </div>
              </div>
            </div>
          </div>
          <div class="w-full mt-6 lg:mt-10">
            <!-- eslint-disable vue/no-v-html -->
            <p
              class="text-lg"
              v-html="auction.description_long"
            />
            <!-- eslint-enable -->
          </div>
        </div>
      </div>

      <template v-if="popularAuctions.length > 0">
        <div class="bg-sky-400">
          <div class="container mx-auto pt-8 pb-6">
            <h2 class="font-bold text-4xl text-white mb-4">
              Populaire Kavels
            </h2>

            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div
                v-for="(popularAuction, i) in popularAuctions"
                :key="popularAuction.id"
              >
                <HomeAuctionBlock
                  :auction="popularAuction"
                  :class="{ 'lg:hidden': popularAuctions.length === 6 && i === popularAuctions.length - 1 }"
                />
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="activeThemeAuctions.length > 0">
        <div class="container mx-auto py-8">
          <div class="flex flex-col md:flex-row md:items-center md:space-x-8 mb-4">
            <h2 class="font-bold text-4xl">
              Lopende Thema Veilingen
            </h2>

            <Link
              class="block text-right md:text-left text-sm underline hover:no-underline"
              :href="route('frontend.index')"
            >
              Alle veilingen
            </Link>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div
              v-for="(activeThemeAuction, i) in activeThemeAuctions"
              :key="activeThemeAuction.slug"
            >
              <HomeThemeAuctionBlock
                :theme-auction="activeThemeAuction"
                :class="{ 'lg:hidden': activeThemeAuctions.length === 6 && i === activeThemeAuctions.length - 1 }"
              />
            </div>
          </div>
        </div>
      </template>
    </Container>
  </div>
</template>

<script>
import {Link} from '@inertiajs/vue2';
import Head from '../../Partials/Head.vue';
import Container from '../../Partials/Container.vue';
import Home from '../../Layouts/Home.vue';
import IndexPageBase from '../../Mixins/IndexPageBase';
import HomeThemeAuctionBlock from '../../Partials/New/HomeThemeAuctionBlock.vue';
import HomeAuctionBlock from '../../Partials/New/HomeAuctionBlock.vue';
import LargerPlaceBid from '../../Partials/Products/LargerPlaceBid.vue';
import AuctionFavorite from '../../Partials/New/AuctionFavorite.vue';

export default {
  components: {
    AuctionFavorite,
    Container,
    Head,
    Link,
    HomeAuctionBlock,
    HomeThemeAuctionBlock,
    LargerPlaceBid,
  },

  mixins: [IndexPageBase],

  layout: Home,

  props: {
    seoTitle: {
      required: true,
      type: String,
    },

    seoDescription: {
      required: true,
      type: String,
    },

    themeAuction: {
      required: true,
      type: Object,
    },

    auction: {
      required: true,
      type: Object,
    },

    popularAuctions: {
      required: true,
      type: Array,
    },

    activeThemeAuctions: {
      required: true,
      type: Array,
    },
  },

  data() {
    return {
      slider: null,
    };
  },

  mounted() {
    this.setUpSlider();
  },

  methods: {
    setUpSlider() {
      if (typeof window === 'undefined' || !this.auction.main_image_big) {
        return;
      }

      import('tiny-slider').then(({tns}) => {
        this.$nextTick(() => {
          if (this.slider) {
            this.slider.rebuild();
          } else {
            this.slider = tns({
              container: '#slider-big',
              controlsContainer: '#slider-controls',
              navContainer: '#slider-small',
              items: 1,
              navAsThumbnails: true,
              autoplay: true,
              autoplayButtonOutput: false,
              autoplayTimeout: 5000,
              mouseDrag: true,
              speed: 400,
            });
          }
        });
      });
    },

    auctionUpdated(newAuction) {
      for (const key in newAuction) {
        if (key === 'latest_bid') {
          this.$set(this.auction, 'customer_has_highest_bid', newAuction[key].customer_id === this.$page.props.customer.id);
        } else {
          this.$set(this.auction, key, newAuction[key]);
        }
      }
    },
  },
};
</script>
